//@ts-nocheck

import { Inertia } from '@inertiajs/inertia'
import { InertiaFormProps, useRemember } from '@inertiajs/inertia-react'
import { useCallback, useRef, useState } from 'react'

export default function useCForm<TForm = Record<string, any>>(...args): InertiaFormProps<TForm> {
  const rememberKey = typeof args[0] === 'string' ? args[0] : null
  const defaults = (typeof args[0] === 'string' ? args[1] : args[0]) || {}
  const cancelToken = useRef(null)
  const recentlySuccessfulTimeoutId = useRef(null)
  const [data, setData] = rememberKey ? useRemember(defaults, `${rememberKey}:data`) : useState(defaults)
  const [errors, setErrors] = rememberKey ? useRemember({}, `${rememberKey}:errors`) : useState({})
  const [hasErrors, setHasErrors] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(null)
  const [wasSuccessful, setWasSuccessful] = useState(false)
  const [recentlySuccessful, setRecentlySuccessful] = useState(false)
  let transform = (callback) => actualTransform = callback;
  let actualTransform = (data) => data;

  const submit = useCallback(
    (method, url, options = {}) => {
      const _options = {
        ...options,
        onCancelToken: (token) => {
          cancelToken.current = token

          if (options.cancelToken) {
            return options.cancelToken(token)
          }
        },
        onBefore: (visit) => {
          setWasSuccessful(false)
          setRecentlySuccessful(false)
          clearTimeout(recentlySuccessfulTimeoutId.current)

          if (options.onBefore) {
            return options.onBefore(visit)
          }
        },
        onStart: (visit) => {
          setProcessing(true)

          if (options.onStart) {
            return options.onStart(visit)
          }
        },
        onProgress: (event) => {
          setProgress(event)

          if (options.onProgress) {
            return options.onProgress(event)
          }
        },
        onSuccess: (page) => {
          setProcessing(false)
          setProgress(null)
          setErrors({})
          setHasErrors(false)
          setWasSuccessful(true)
          setRecentlySuccessful(true)
          recentlySuccessfulTimeoutId.current = setTimeout(() => setRecentlySuccessful(false), 2000)

          if (options.onSuccess) {
            return options.onSuccess(page)
          }
        },
        onError: (errors) => {
          setProcessing(false)
          setProgress(null)
          setErrors(errors)
          setHasErrors(true)

          if (options.onError) {
            return options.onError(errors)
          }
        },
        onCancel: () => {
          setProcessing(false)
          setProgress(null)

          if (options.onCancel) {
            return options.onCancel()
          }
        },
        onFinish: () => {
          cancelToken.current = null

          if (options.onFinish) {
            return options.onFinish()
          }
        },
      }

      if (method === 'delete') {
        Inertia.delete(url, { ..._options, data: actualTransform(data) })
      } else {
        Inertia[method](url, actualTransform(data), _options)
      }
    },
    [data, setErrors, actualTransform],
  )

  return {
    data,
    setData(key, value) {
      if (typeof key === 'string') {
        setData({ ...data, [key]: value })
      } else if (typeof key === 'function') {
        setData(data => key(data))
      } else {
        setData(key)
      }
    },
    errors,
    hasErrors,
    processing,
    progress,
    wasSuccessful,
    recentlySuccessful,
    transform,
    reset(...fields) {
      if (fields.length === 0) {
        setData(defaults)
      } else {
        setData(
          Object.keys(defaults)
            .filter((key) => fields.includes(key))
            .reduce((carry, key) => {
              carry[key] = defaults[key]
              return carry
            }, {}),
        )
      }
    },
    clearErrors(...fields) {
      setErrors(
        Object.keys(errors).reduce(
          (carry, field) => ({
            ...carry,
            ...(fields.length > 0 && !fields.includes(field) ? { [field]: errors[field] } : {}),
          }),
          {},
        ),
      )
      setHasErrors(Object.keys(errors).length > 0)
    },
    setErrors,
    submit,
    get(url, options) {
      submit('get', url, options)
    },
    post(url, options) {
      submit('post', url, options)
    },
    put(url, options) {
      submit('put', url, options)
    },
    patch(url, options) {
      submit('patch', url, options)
    },
    delete(url, options) {
      submit('delete', url, options)
    },
    cancel() {
      if (cancelToken.current) {
        cancelToken.current.cancel()
      }
    },
  }
}
